// © Microsoft Corporation. All rights reserved.
let storage = new Map();
function getItem(key, fallback) {
    let found = storage.get(key);
    if (found === void 0 && fallback) {
        found = fallback();
        storage.set(key, found);
    }
    return found;
}
/**
 * Create a StorageBackend instance that wraps a getter function for the actual backend.
 */
export function createStoreWrapper(getBackend) {
    return {
        get: (key) => getBackend().get(key),
        set: (key, value) => getBackend().set(key, value),
        delete: (key) => getBackend().delete(key),
        clear: () => getBackend().clear(),
        has: (key) => getBackend().has(key)
    };
}
export const IsomorphicStorage = Object.freeze({
    /**
     * Stores a value in storage.
     * @param key - The key to store the value under.
     * @param value - The value to store.
     * @returns This RequestStorage instance.
     */
    set(key, value) {
        storage.set(key, value);
        return this;
    },
    /**
     * Gets a value from storage.
     * @param key - The key to get the value for.
     * @param fallback - A function that provides a value if there is nothing stored under the key.
     * @returns The value stored under the key.
     * @remarks
     * The return result of the fallback function will be stored under the key for later access.
     */
    get: getItem,
    /**
     * Clears all values.
     */
    clear() {
        storage.clear();
    },
    /**
     *
     * @param key - The key to delete.
     * @returns true if the value was deleted; false otherwise.
     */
    delete(key) {
        return storage.delete(key);
    },
    /**
     * Determines whether there's a value for the given key.
     * @param key - The key to check.
     * @returns true if the key exists; false otherwise
     */
    has(key) {
        return storage.has(key);
    }
});
export function setStorageBackend(backend) {
    storage = backend;
}
/**
 * Create an IsomorphicStorage-based value reference
 * @param value - The initial value
 * @returns { current: T } the object contains getter & setter to current value
 */
export function useIsomorphicStorageRef(value) {
    const key = Symbol();
    return {
        get current() {
            return IsomorphicStorage.get(key, () => value);
        },
        set current(nextValue) {
            IsomorphicStorage.set(key, nextValue);
        }
    };
}
//# sourceMappingURL=IsomorphicStorage.js.map